/**
 * Bump on any change that alters analysis output. Stored analyses with another version are
 * re-analyzed on open (IMPLEMENTATION_PLAN.md section 4.3), and golden-file tests are keyed by it.
 */
export const ENGINE_VERSION = '1.4.1';

/** Analysis input: mono at this rate (prepared on the main thread with OfflineAudioContext). */
export const ANALYSIS_SAMPLE_RATE = 22_050;

export interface EngineParams {
  tuning: {
    fftSize: number;
    hop: number;
    frameStep: number;
    minHz: number;
    maxHz: number;
    peakRel: number;
  };
  spectrum: {
    fftSize: number;
    hop: number;
    minMidi: number;
    maxMidi: number;
    /** Max distance (in semitones) of an FFT bin from a semitone centre to count towards it. */
    binTolerance: number;
  };
  hpss: { enabled: boolean; timeWidth: number; freqWidth: number };
  chroma: {
    logGain: number;
    /** Highest MIDI note counted as bass (55 = G3). */
    bassMaxMidi: number;
  };
  onset: {
    fftSize: number;
    hop: number;
    maxHz: number;
    /** Upper edge of the low band used for downbeat accents (kick drum, bass). */
    lowMaxHz: number;
    logGain: number;
    detrendFrames: number;
  };
  tempo: {
    minBpm: number;
    maxBpm: number;
    priorBpm: number;
    priorOctaves: number;
    /** Prefer the double tempo when the estimate is below this and the double is nearly as strong. */
    doubleBelowBpm: number;
    doubleMinRatio: number;
  };
  beats: {
    tightness: number;
    /** Shift beats by half a period when chords change this much more between beats than on them. */
    halfShiftRatio: number;
  };
  chords: {
    emissionBeta: number;
    selfTransition: number;
    noChordScore: number;
    /** A beat quieter than this fraction of the loudest beat counts as silent. */
    silenceRel: number;
    /** A beat whose chroma energy is below this fraction of the strongest beat counts as silent. */
    quietChromaRel: number;
    /** Overtone weight in chord templates (0 = plain three-note templates). */
    templateHarmonicDecay: number;
    /** Bonus for chords whose root is the strongest bass pitch (share of the bass chroma). */
    bassWeight: number;
    /** "majmin" decodes major and minor only (MVP); "full" adds sevenths, sus, dim, aug, … (F2.4). */
    vocabulary: 'majmin' | 'full';
    /** Score penalty for 7, maj7 and m7 against plain triads. */
    seventhPenalty: number;
    /** Score penalty for 6, add9, sus2, sus4, dim and aug. */
    colorPenalty: number;
  };
  key: { chordWeight: number; tonicWeight: number; boundaryWeight: number };
  downbeats: {
    changeWeight: number;
    onsetWeight: number;
    /** How much better 3/4 must score than 4/4 to be chosen. */
    threeFourMargin: number;
  };
}

export const DEFAULT_PARAMS: EngineParams = {
  tuning: { fftSize: 8192, hop: 1024, frameStep: 4, minHz: 100, maxHz: 1500, peakRel: 0.1 },
  // Up to F5 only: chords sit below that, sung and played melodies often above it.
  spectrum: { fftSize: 8192, hop: 1024, minMidi: 36, maxMidi: 77, binTolerance: 0.4 },
  hpss: { enabled: true, timeWidth: 9, freqWidth: 9 },
  chroma: { logGain: 100, bassMaxMidi: 55 },
  onset: { fftSize: 2048, hop: 256, maxHz: 8000, lowMaxHz: 250, logGain: 100, detrendFrames: 20 },
  tempo: {
    minBpm: 50,
    maxBpm: 220,
    priorBpm: 120,
    priorOctaves: 0.9,
    doubleBelowBpm: 85,
    doubleMinRatio: 0.75,
  },
  beats: { tightness: 100, halfShiftRatio: 1.5 },
  chords: {
    emissionBeta: 12,
    selfTransition: 0.85,
    noChordScore: 0.55,
    silenceRel: 0.01,
    quietChromaRel: 0.02,
    templateHarmonicDecay: 0,
    bassWeight: 0.25,
    vocabulary: 'full',
    seventhPenalty: 0.05,
    colorPenalty: 0.2,
  },
  key: { chordWeight: 1, tonicWeight: 0.5, boundaryWeight: 0.3 },
  downbeats: { changeWeight: 1, onsetWeight: 0.5, threeFourMargin: 0.4 },
};

export type ParamOverrides = { [S in keyof EngineParams]?: Partial<EngineParams[S]> };

export function withParams(overrides: ParamOverrides = {}): EngineParams {
  const out = structuredClone(DEFAULT_PARAMS);
  for (const section of Object.keys(overrides) as (keyof EngineParams)[]) {
    Object.assign(out[section], overrides[section]);
  }
  return out;
}
